import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { TruncatedText } from "@/components/shared/truncated-text";
import { ColumnDef } from "@tanstack/react-table";
import { Gem } from "lucide-react";
import { formatTimestamp } from "utils";
import { hasPremiumData } from "../clip-ui/clip-menu";
import { ClipConfiguration, Status } from "../clip-ui/models";
import { getStatusColor } from "../clip-utils";
import { RowActionsMenu } from "./menus";

interface GetColumnsParams {
  nonEditableStatuses: Status[];
  onDelete: (clip: ClipConfiguration) => void;
}

export const getClipManagementColumns = ({
  nonEditableStatuses,
  onDelete,
}: GetColumnsParams): ColumnDef<ClipConfiguration>[] => [
  {
    accessorKey: "name",
    header: "Name",
    size: 200,
    cell: ({ row }) => (
      <div className="flex items-center h-full min-w-0">
        <TruncatedText className="max-w-full">{row.getValue("name")}</TruncatedText>
      </div>
    ),
  },
  {
    accessorKey: "clientReference",
    header: "Client Ref",
    size: 150,
    cell: ({ row }) => (
      <div className="flex items-center h-full min-w-0">
        <TruncatedText className="max-w-full">
          {row.getValue("clientReference") || "-"}
        </TruncatedText>
      </div>
    ),
  },
  {
    accessorKey: "totalCredits",
    header: "Credits",
    size: 100,
    cell: ({ row }) => {
      const clip = row.original as ClipConfiguration;
      return (
        <div className="flex items-center h-full">
          <span className={clip.totalCredits ? "" : "text-muted-foreground"}>
            {clip.totalCredits ? clip.totalCredits.toLocaleString() : "-"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "totalDollars",
    header: "Cost ($)",
    size: 100,
    cell: ({ row }) => {
      const clip = row.original as ClipConfiguration;
      return (
        <div className="flex items-center h-full">
          <span className={clip.totalDollars ? "" : "text-muted-foreground"}>
            {clip.totalDollars
              ? `$${clip.totalDollars.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "-"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    size: 120,
    cell: ({ row }) => {
      const status = row.getValue("status") as Status;
      return (
        <div className="flex items-center h-full">
          <Badge variant="outline" className={getStatusColor(status)}>
            {status}
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "premium",
    header: "Premium",
    size: 100,
    enableSorting: false,
    cell: ({ row }) => {
      const clip = row.original as ClipConfiguration;
      const isPremium = hasPremiumData(clip);
      return (
        <div className="flex items-center h-full">
          {isPremium && <Gem size={18} className="text-primary" />}
        </div>
      );
    },
  },
  {
    accessorKey: "author",
    header: "Author",
    size: 150,
    cell: ({ row }) => {
      const clip = row.original as ClipConfiguration;
      const author = clip.authorName || clip.developer || "";
      if (!author) {
        return <div className="flex items-center h-full" />;
      }
      return (
        <div className="flex items-center gap-2 h-full min-w-0">
          <Avatar className="h-6 w-6 shrink-0">
            <AvatarFallback className="text-xs">
              {author.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <TruncatedText className="max-w-full">{author}</TruncatedText>
        </div>
      );
    },
  },
  {
    accessorKey: "lastEditedAt",
    header: "Modified",
    size: 100,
    cell: ({ row }) => {
      const clip = row.original as ClipConfiguration;
      const timestamp = clip.lastEditedAt
        ? clip.lastEditedAt * 1000
        : new Date(clip.date).getTime();
      return (
        <div className="flex items-center h-full">
          <span>{formatTimestamp(timestamp)}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "purchasedOn",
    header: "Purchased",
    size: 100,
    sortingFn: (rowA, rowB) => {
      const clip1 = rowA.original as ClipConfiguration;
      const clip2 = rowB.original as ClipConfiguration;
      const timestamp1 = clip1.purchasedOn
        ? clip1.purchasedOn * 1000
        : clip1.status === "Completed"
          ? new Date(clip1.date).getTime()
          : 0;
      const timestamp2 = clip2.purchasedOn
        ? clip2.purchasedOn * 1000
        : clip2.status === "Completed"
          ? new Date(clip2.date).getTime()
          : 0;
      return timestamp1 - timestamp2;
    },
    cell: ({ row }) => {
      const clip = row.original as ClipConfiguration;
      const timestamp = clip.purchasedOn
        ? clip.purchasedOn * 1000
        : clip.status === "Completed"
          ? new Date(clip.date).getTime()
          : null;
      return (
        <div className="flex items-center h-full">
          <span className={timestamp ? "" : "text-muted-foreground"}>
            {timestamp ? formatTimestamp(timestamp) : "-"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "completedOn",
    header: "Completed",
    size: 100,
    sortingFn: (rowA, rowB) => {
      const clip1 = rowA.original as ClipConfiguration;
      const clip2 = rowB.original as ClipConfiguration;
      const timestamp1 = clip1.completedOn
        ? clip1.completedOn * 1000
        : clip1.status === "Completed"
          ? new Date(clip1.date).getTime()
          : 0;
      const timestamp2 = clip2.completedOn
        ? clip2.completedOn * 1000
        : clip2.status === "Completed"
          ? new Date(clip2.date).getTime()
          : 0;
      return timestamp1 - timestamp2;
    },
    cell: ({ row }) => {
      const clip = row.original as ClipConfiguration;
      const timestamp = clip.completedOn
        ? clip.completedOn * 1000
        : clip.status === "Completed"
          ? new Date(clip.date).getTime()
          : null;
      return (
        <div className="flex items-center h-full">
          <span className={timestamp ? "" : "text-muted-foreground"}>
            {timestamp ? formatTimestamp(timestamp) : "-"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "actions",
    header: "",
    size: 60,
    enableSorting: false,
    meta: { sticky: "right" },
    cell: ({ row }) => (
      <div className="flex items-center h-full justify-end">
        <RowActionsMenu
          clip={row.original as ClipConfiguration}
          nonEditableStatuses={nonEditableStatuses}
          onDelete={onDelete}
        />
      </div>
    ),
  },
];
